// src/encounter/lib/readout-lines.ts
// PROD-2816 (gap G3) — behind the `frameV2` design switch ONLY. Design-desk rule D-45.
//
// A Template Studio READOUT is an item the clinician does not answer: it DISPLAYS a value that is
// computed or sourced elsewhere (a quantity carrying a LOINC code and a unit). This codebase's
// readout marker is the authored FHIR `readOnly` flag — the same predicate the save filter uses
// (qr-item-meta.ts, to-questionnaire-response-resource.ts; PROD-2129 D-S10b). The `readout`
// itemControl code is allowlisted on the wire but the DS has no role for it yet, so `readOnly`
// is the only marker that reaches this app.
//
// The DS renderer draws a read-only item as a DISABLED INPUT. With no value behind it that is an
// empty box in the physician's encounter — visual noise (Samer, first-hand).
//
// D-45: a readout is NEVER hidden. This app has no resolver for readouts yet, so every unresolved
// one is "not available", not "empty", and must say so. So, in the RENDERED questionnaire only:
//  • no value  → ONE compact muted line: the authored label + "not available in this view yet";
//  • a value   → the label and the value as read-only text, not a disabled input.
// Both are drawn by the DS's own static-text branch (DS `HelperText`: DS text tokens, no new
// colours). Nothing is removed, so no group header disappears. The full template still drives the
// QR serialisation (`buildQrItemMeta` reads the FHIR template), exactly like the gender scope
// (PROD-1497); a readout is never saved anyway. The DS keeps every value in its own map whatever
// the item's type, so a hydrated readout value is not lost by the type change.
//
// DEC-002 (a static-text item never carries a terminology code): the line STRIPS `code`, and it
// lives only in the rendered questionnaire handed to the DS renderer — never written to
// QuestionBank or any FHIR store. Pinned by a test.
//
// LEFT EXACTLY AS THE DS DRAWS IT TODAY — each is a value that exists, or might, or a question:
//  • anything not `readOnly: true` (every editable question, whatever its code or type);
//  • a `required` item;
//  • an item with a `calculatedExpression` — the DS computes its value at render time;
//  • an item with an authored `initial` value;
//  • an item another item's `enableWhen` reads — a source keeps its type so that condition is
//    evaluated exactly as before;
//  • static-text / group / choice / boolean / attachment items — only value-box types are readouts.
//
// The proper fix — a DS readout role with resolvers that fetch the value from the chart — comes
// from the design desk. This is the stop-gap until then. `always-show`: no such marker exists in
// this codebase or on the wire, and under D-45 every readout shows a line anyway.
import type { Questionnaire, QItem, QItemType, QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';

/** The words after the label on an unresolved readout (D-45). */
export const READOUT_NOT_AVAILABLE = 'not available in this view yet';

/** The DS item type drawn as one line of static text (DS `HelperText`). */
export const STATIC_LINE_TYPE: QItemType = 'display';

/** Value-box item types the Studio authors a readout as. Anything else is never a readout here. */
const READOUT_TYPES: ReadonlySet<string> = new Set(['quantity', 'decimal', 'integer', 'string', 'text']);

/** The value as a clinician would read it, or `undefined` when there is nothing to show. `0` counts. */
function formatValue(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value.trim() === '' ? undefined : value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value.toISOString().slice(0, 10);
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : undefined;
  return undefined;
}

function collectEnableWhenSources(items: readonly QItem[], into: Set<string>): Set<string> {
  for (const item of items) {
    for (const cond of item.enableWhen ?? []) into.add(cond.question);
    if (item.item) collectEnableWhenSources(item.item, into);
  }
  return into;
}

/** The score-band code system a scored total carries (the Morse total, the Wong-Baker bands). */
export const SCORE_BAND_SYSTEM = 'https://fhir.scrips.com/CodeSystem/cps-score-band';

function isReadout(item: QItem, sources: ReadonlySet<string>): boolean {
  if (item.readOnly !== true) return false;
  // PROD-2947 (6 Oct contract K4): a scored total (it carries its bands as `cps-score-band` codes — the Morse total) is
  // a computed score the note draws as "N · Band" from the answers, never a readout line. Turned into a static line, the
  // Fall assessment lost its total on dev (fallAssessmentParts no longer found it).
  if ((item.code ?? []).some((c) => c.system === SCORE_BAND_SYSTEM)) return false;
  if (item.required === true) return false;
  if (!READOUT_TYPES.has(item.type)) return false;
  if (item.calculatedExpression != null && item.calculatedExpression !== '') return false;
  if ((item.initial ?? []).length > 0) return false;
  return !sources.has(item.linkId);
}

/** linkId → the displayable value, for every answer that has one. */
export function readoutValues(response: QuestionnaireResponse | undefined): Map<string, string> {
  const out = new Map<string, string>();
  for (const a of response?.answers ?? []) {
    const shown = formatValue(a.value);
    if (shown !== undefined) out.set(a.linkId, shown);
  }
  return out;
}

/**
 * A stable string naming each read-only item's displayable value. Changes only when a readout's
 * value changes — so a caller can memoise `toReadoutLines` on it and keep the rendered
 * questionnaire's identity across ordinary keystrokes (the DS renderer memoises on that identity).
 */
export function readoutValuesKey(q: Questionnaire | undefined, response: QuestionnaireResponse | undefined): string {
  if (!q) return '';
  const values = readoutValues(response);
  const parts: string[] = [];
  const walk = (items: readonly QItem[]): void => {
    for (const item of items) {
      const v = item.readOnly === true ? values.get(item.linkId) : undefined;
      if (v !== undefined) parts.push(`${item.linkId}\u0001${v}`);
      if (item.item) walk(item.item);
    }
  };
  walk(q.item);
  return parts.join('\u0000');
}

/** Parses a `readoutValuesKey` back into the map `toReadoutLines` reads. */
export function parseReadoutValuesKey(key: string): Map<string, string> {
  const out = new Map<string, string>();
  if (key === '') return out;
  for (const part of key.split('\u0000')) {
    const at = part.indexOf('\u0001');
    if (at > 0) out.set(part.slice(0, at), part.slice(at + 1));
  }
  return out;
}

/** The readout drawn as one line of text: its value, or the honest "not available". */
function toLine(item: QItem, value: string | undefined): QItem {
  const label = item.text ?? item.linkId;
  const text =
    value !== undefined
      ? `${label}: ${value}${item.unit ? ` ${item.unit}` : ''}`
      : `${label} — ${READOUT_NOT_AVAILABLE}`;
  // Keep linkId, enableWhen and layout placement; drop what a static line must not carry — its
  // terminology binding above all (DEC-002). The template itself keeps every field.
  const { code: _code, unit: _unit, readOnly: _readOnly, itemControl: _itemControl, ...rest } = item;
  void _code;
  void _unit;
  void _readOnly;
  void _itemControl;
  return { ...rest, type: STATIC_LINE_TYPE, text };
}

/**
 * The questionnaire with every readout drawn as one line of text instead of a disabled input.
 * Nothing is removed. Returns `q` BY REFERENCE when it holds no readout.
 */
export function toReadoutLines(q: Questionnaire, values: ReadonlyMap<string, string>): Questionnaire {
  const sources = collectEnableWhenSources(q.item, new Set());
  let changed = false;
  const walk = (items: readonly QItem[]): QItem[] =>
    items.map((item) => {
      if (isReadout(item, sources)) {
        changed = true;
        return toLine(item, values.get(item.linkId));
      }
      if (item.item && item.item.length > 0) {
        const kids = walk(item.item);
        if (kids.some((k, i) => k !== item.item?.[i])) return { ...item, item: kids };
      }
      return item;
    });
  const item = walk(q.item);
  return changed ? { ...q, item } : q;
}
